/**
 * Abbreviates a number into a string with K, M, B suffixes.
 * @param {number | string} input - The number or string to be abbreviated.
 * @returns {string} The abbreviated string.
 */
export function abbreviateNumber(input: number | string): string {
  if (!input) {
    return '0';
  }

  if (typeof input === 'string') {
    // Try to convert the string to a number.
    const num = parseFloat(input);

    // Check if the conversion was successful.
    if (!isNaN(num)) {
      // Call recursively with the converted number.
      return abbreviateNumber(num);
    } else {
      // If the conversion fails, return an error message.
      return '0';
    }
  }

  /**
   * Array of suffixes for thousands, millions, billions, etc.
   * @type {string[]}
   */
  const abbreviations = ['', 'K', 'M', 'B'];

  // If the number is less than 1000, return it as is.
  if (input < 1000) {
    return input.toString();
  }

  // Calculate the tier (K, M, B).
  const tier = Math.floor(Math.log10(input) / 3);

  // Select the appropriate suffix based on the tier.
  const suffix = abbreviations[tier];

  // Calculate the scale to divide the number by.
  const scale = Math.pow(10, tier * 3);

  // Divide the number by the scale, round it, and convert to uppercase.
  const scaledNumber =
    tier > 0 ? Math.floor(input / scale) : Math.round(input / scale);

  // Convert the scaled number to a string and append the suffix.
  return scaledNumber.toString().toUpperCase() + suffix;
}
